import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { UserService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

// Simplified validation schema for admin users
const adminSchema = yup.object({
    firstName: yup.string().required('formErrors.firstNameRequired'),
    lastName: yup.string().required('formErrors.lastNameRequired'),
    email: yup.string().email('formErrors.invalidEmail').required('formErrors.emailRequired'),
    phone: yup.string().optional(),
    city: yup.string().optional(),
    zip: yup.string().optional(),
    address: yup.string().optional(),
    addressSuffix: yup.string().optional(),
    password: yup.string().when("$isEdit", ([isEdit], schema) => {
        return isEdit ? schema.optional() : schema.required('formErrors.passwordRequired');
    }),
    isActive: yup.boolean().required('formErrors.statusRequired'),
});

type AdminUserState = yup.InferType<typeof adminSchema>;

const AdminForm: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const [userData, setUserData] = useState<AdminUserState | null>(null);

    const { control, handleSubmit, reset, formState: { errors } } = useForm<AdminUserState>({
        resolver: yupResolver(adminSchema),
        context: { isEdit },
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            city: "",
            zip: "",
            address: "",
            addressSuffix: "",
            password: "",
            isActive: true,
        },
    });

    // Handle user data for editing
    useEffect(() => {
        if (isEdit) {
            UserService.getSingle(+id!)
                .then((response) => {
                    const user = response;
                    setUserData({
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        phone: user.contact?.phone || "",
                        city: user.contact?.city || "",
                        zip: user.contact?.zip || "",
                        address: user.contact?.address || "",
                        addressSuffix: user.contact?.addressSuffix || "",
                        password: "",
                        isActive: user.isActive,
                    });
                })
                .catch((error) => {
                    ErrorToast(error);
                });
        }
    }, [isEdit, id]);

    // Reset form when userData is loaded
    useEffect(() => {
        if (userData) {
            reset(userData);
        }
    }, [userData, reset]);

    const onSubmit = async (data: AdminUserState) => {
        const formData = new FormData();

        // Prepare data with hardcoded ROLE_ADMIN and contact object
        const adminData = {
            ...data,
            roles: ["ROLE_ADMIN"], // Hardcoded admin role
            company: null, // No company for admin users
            contact: {
                phone: data.phone || "",
                city: data.city || "",
                zip: data.zip || "",
                address: data.address || "",
                addressSuffix: data.addressSuffix || "",
            },
        };

        // Append the data as JSON
        formData.append("body", JSON.stringify(adminData));

        try {
            isEdit
                ? await UserService.update(formData, +id!)
                : await UserService.create(formData);

            SuccessToast(t(`user.${isEdit ? "update" : "create"}`));
            navigate("/users");
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card className="rounded-top-0 border-top-0">
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <FormErrorSummary errors={errors} />
                                <Row className="g-3">
                                    <Col sm={12}>
                                        <div className="mb-4">
                                            <Row className="g-3">
                                                {/* Basic Information */}
                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">
                                                            {t("user.firstName")} <span className="text-danger">*</span>
                                                        </label>
                                                        <Controller
                                                            name="firstName"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.firstName")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.firstName && <div className="invalid-feedback">
                                                            {t(errors.firstName?.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">
                                                            {t("user.lastName")} <span className="text-danger">*</span>
                                                        </label>
                                                        <Controller
                                                            name="lastName"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.lastName")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.lastName && <div className="invalid-feedback">
                                                            {t(errors.lastName?.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">
                                                            {t("user.email")} <span className="text-danger">*</span>
                                                        </label>
                                                        <Controller
                                                            name="email"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="email"
                                                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.email")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.email && <div className="invalid-feedback">
                                                            {t(errors.email?.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">{t("user.phone")}</label>
                                                        <Controller
                                                            name="phone"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.phone")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.phone && <div className="invalid-feedback">
                                                            {t(errors.phone.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">{t("user.city")}</label>
                                                        <Controller
                                                            name="city"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.city")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.city && <div className="invalid-feedback">
                                                            {t(errors.city.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">{t("user.zip")}</label>
                                                        <Controller
                                                            name="zip"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors.zip ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.zip")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.zip && <div className="invalid-feedback">
                                                            {t(errors.zip.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">{t("user.address")}</label>
                                                        <Controller
                                                            name="address"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.address")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.address && <div className="invalid-feedback">
                                                            {t(errors.address.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={6}>
                                                    <div>
                                                        <label className="form-label">{t("user.addressSuffix")}</label>
                                                        <Controller
                                                            name="addressSuffix"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors.addressSuffix ? 'is-invalid' : ''}`}
                                                                    placeholder={t("user.addressSuffix")}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />
                                                        {errors.addressSuffix && <div className="invalid-feedback">
                                                            {t(errors.addressSuffix.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                {!isEdit && (
                                                    <Col sm={6}>
                                                        <div>
                                                            <label className="form-label">
                                                                {t("user.password")} <span className="text-danger">*</span>
                                                            </label>
                                                            <Controller
                                                                name="password"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="password"
                                                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                                        placeholder={t("user.password")}
                                                                        {...field}
                                                                    />
                                                                )}
                                                            />
                                                            {errors.password && <div className="invalid-feedback">
                                                                {t(errors.password.message as string)}
                                                            </div>}
                                                        </div>
                                                    </Col>
                                                )}

                                                {/* Status Toggle */}
                                                <Col sm={6} className="mt-4">
                                                    <div>
                                                        <label className="form-label">{t("user.status")}</label>
                                                        <div className="form-check form-switch">
                                                            <Controller
                                                                name="isActive"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id="isActive"
                                                                        checked={field.value || false}
                                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                                    />
                                                                )}
                                                            />
                                                            <label className="form-check-label" htmlFor="isActive">
                                                                {t("user.active")}
                                                            </label>
                                                        </div>
                                                        {errors.isActive && <div className="invalid-feedback">
                                                            {t(errors.isActive.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>

                                                <Col sm={12} className="mt-4">
                                                    <Button type="submit" variant="primary">
                                                        {isEdit ? t("user.update") : "Create Superadmin"}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default AdminForm;
